<template>
    <div class="l-goal-details-key-result-edit">
        <div class="row">
            <div class="col-md-12" style="display: flex; align-items: center;">
                <!-- Name -->
                <l-input-text
                    v-model="kr.name"
                    type="text"
                    style="flex-grow: 1"
                    :disabled="!goal.access.canUpdateGoal"
                    :label="``"
                    :placeholder="$t(`goals.update.placeholderMetricName`)"
                    hide-help-block
                />
                <!--/Name -->

                <!-- Extras -->
                <span class="btn-group l-default-margin-s">
                    <b-dropdown>
                        <template #button-content>
                            <span class="color-primary" style="font-size: 18px;">
                                <i class="fal fa-cog tw-ml-2 right only"></i>
                            </span>
                        </template>

                        <b-dropdown-item
                            class="clickable"
                            data-test-class="kr-show-owner"
                            @click="
                                () => {
                                    showOwner = true;
                                }
                            "
                        >
                            <i class="fal fa-user"></i>
                            <span>{{ $t("goals.update.labelAddKROwner") }}</span>
                        </b-dropdown-item>
                        <b-dropdown-item
                            class="clickable"
                            data-test-class="kr-show-description"
                            @click="
                                () => {
                                    showDescription = true;
                                }
                            "
                        >
                            <i class="fal fa-file"></i>
                            <span>{{ $t("goals.update.labelAddDescription") }}</span>
                        </b-dropdown-item>
                        <b-dropdown-item
                            v-show="kr.type !== 'initiative'"
                            class="clickable"
                            data-test-class="kr-show-weight"
                            @click="
                                () => {
                                    showWeight = true;
                                }
                            "
                        >
                            <i class="fal fa-percent"></i>
                            <span>{{ $t("goals.update.labelAddWeight") }}</span>
                        </b-dropdown-item>
                        <b-dropdown-item
                            class="clickable"
                            data-test-class="kr-show-deadline"
                            @click="
                                () => {
                                    showDeadline = true;
                                }
                            "
                        >
                            <i class="fal fa-calendar"></i>
                            <span>{{ $t("goals.update.labelAddDeadline") }}</span>
                        </b-dropdown-item>
                    </b-dropdown>
                </span>
                <!--/Extras -->

                <!-- Remove -->
                <span
                    class="l-default-margin-s color-primary clickable"
                    style="font-size: 18px; margin-bottom: 2px;"
                    tabindex="0"
                    @click="removeKeyResult()"
                    @keyup.enter="removeKeyResult()"
                >
                    <i class="fal fa-trash"></i>
                </span>
                <!--/Remove -->
            </div>
        </div>

        <!-- Description -->
        <div v-show="showDescription || kr.description" class="row">
            <div class="col-xs-12">
                <l-input-text
                    v-model="kr.description"
                    type="textarea"
                    :rows="2"
                    :disabled="!goal.access.canUpdateGoal"
                    :label="``"
                    :placeholder="$t(`goals.update.placeholderDescription`)"
                    optional
                />
            </div>
        </div>
        <!--/Description -->

        <l-space :size="1" />

        <!-- Metric -->
        <div class="row">
            <!-- Metric Type -->
            <div class="col-xs-2">
                <l-select
                    v-model="kr.metricType"
                    :label="$t(`goals.update.optionSelectMetric`)"
                    :disabled="!goal.access.canUpdateGoal"
                    :options="optionsMetricType"
                    class="tw-mb-8"
                    @change="e => selectKeyResultMetric(e.target.value)"
                />
            </div>
            <!--/Metric Type -->

            <!-- Metric Start -->
            <div v-show="kr.metricType !== 'none'" class="col-xs-2">
                <l-input-text
                    v-model="kr.metricStart"
                    type="number"
                    :label="$t(`goals.update.placeholderMetricStart`)"
                    placeholder="0"
                    :disabled="!goal.access.canUpdateGoal"
                    optional
                />
            </div>
            <!--/Metric Start -->

            <!-- Metric End -->
            <div v-show="kr.metricType !== 'none'" class="col-xs-2">
                <l-input-text
                    ref="metricEnd"
                    v-model="kr.metricEnd"
                    type="number"
                    :label="$t(`goals.update.placeholderMetricEnd`)"
                    placeholder="100"
                    :disabled="!goal.access.canUpdateGoal"
                />
            </div>
            <!--/Metric End -->

            <!-- Metric Weight -->
            <div
                v-show="(kr.metricWeight || showWeight) && kr.type !== 'initiative'"
                class="col-xs-2"
            >
                <l-input-text
                    v-model="kr.metricWeight"
                    type="number"
                    :label="$t(`goals.update.placeholderMetricWeight`)"
                    placeholder="50"
                    :disabled="!goal.access.canUpdateGoal"
                    @input="checkKeyResultWeights()"
                />
            </div>
            <!--/Metric Weight -->

            <!-- Owner -->
            <div v-if="kr.owner || showOwner" class="col-xs-2">
                <l-input-typeahead
                    v-if="!kr.owner"
                    style="margin-top: 0"
                    :label="$t(`goals.update.labelAddKROwner`)"
                    :placeholder="$t(`goals.update.placeholderKROwner`)"
                    :data="owners"
                    :on-select="typeaheadOwnerOnSelect"
                    :on-input-change="typeaheadOwnerOnInputChange"
                />
                <div v-else style="display: flex; align-items: center;">
                    <l-input-text
                        type="text"
                        style="flex-grow: 1"
                        :label="$t(`goals.update.labelAddKROwner`)"
                        :value="kr.owner"
                        disabled
                    />
                    <span
                        v-if="kr.owner"
                        class="l-default-margin-s color-primary clickable"
                        tabindex="0"
                        @click="removeOwner()"
                        @keyup.enter="removeOwner()"
                    >
                        <i class="fal fa-pencil"></i>
                    </span>
                </div>
            </div>
            <!--/Owner -->

            <!-- Deadline -->
            <div v-show="kr.deadline || showDeadline" class="col-xs-2">
                <l-date-picker
                    :label="$t(`goals.update.placeholderDeadline`)"
                    :placeholder="``"
                    :disabled="!goal.access.canUpdateGoal"
                    :value="kr.deadline ? new Date(kr.deadline) : null"
                    :on-date-change="
                        date => {
                            kr.deadline = date;
                        }
                    "
                />
            </div>
            <!--/Deadline -->
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import isEqual from "lodash.isequal";

import LDatePicker from "@/components/LDatePicker";
import LInputText from "@/components/LInputText";
import LInputTypeahead from "@/components/LInputTypeahead";
import LSelect from "@/components/LSelect";
import LSpace from "@/components/LSpace";

import teamPicture from "@/img/icons/team-icon.png";

export default {
    components: {
        LDatePicker,
        LInputText,
        LInputTypeahead,
        LSelect,
        LSpace,
    },
    props: {
        goal: {
            type: Object,
            required: true,
        },
        keyResult: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            kr: {},

            showOwner: false,
            showDescription: false,
            showWeight: false,
            showDeadline: false,
        };
    },
    computed: {
        ...mapGetters({
            usersSearchList: "users/search/list",
            teamsSearchList: "teams/search/list",
        }),
        optionsMetricType() {
            return [
                { value: "none", text: this.$t(`goals.update.optionAchieved`) },
                { value: "percentage", text: this.$t(`goals.update.optionPercentage`) },
                { value: "numerical", text: this.$t(`goals.update.optionNumerical`) },
                { value: "currency", text: this.$t(`goals.update.optionCurrency`) },
            ];
        },
        owners() {
            return [
                ...this.usersSearchList.map(user => ({
                    _id: user._id,
                    type: "user",
                    firstname: user.firstname,
                    lastname: user.lastname,
                    name: user.firstname + " " + user.lastname,
                    desc: user.teamRole.title,
                    picture: user.picture,
                })),
                ...this.teamsSearchList.map(team => ({
                    _id: team._id,
                    type: "team",
                    name: team.name,
                    desc: this.$t("typeahead.team"),
                    picture: teamPicture,
                })),
            ];
        },
    },
    watch: {
        keyResult: {
            immediate: true,
            handler(val) {
                let owner = "";
                if (val.ownerUser) {
                    owner = val.ownerUser.firstname + " " + val.ownerUser.lastname;
                } else if (val.ownerTeam) {
                    owner = val.ownerTeam.name;
                }

                this.kr = {
                    ...val,
                    owner,
                    // We need to add these values here to make them reactive!
                    description: val.description || "",
                    deadline: val.deadline || null,
                };
            },
        },
        kr: {
            deep: true,
            handler(val) {
                if (!isEqual(val, this.keyResult)) {
                    this.$emit("update", val);
                }
            },
        },
    },
    methods: {
        removeKeyResult() {
            this.$emit("remove", this.kr._id);
        },
        selectKeyResultMetric(metricType) {
            if (metricType === "none" || metricType === "percentage") {
                this.kr.metricCurrent = "0";
                this.kr.metricEnd = "100";
            } else {
                this.kr.metricCurrent = "";
                this.kr.metricEnd = "";
            }
            this.$nextTick(() => {
                this.$refs.metricEnd.validate();
            });
        },
        checkKeyResultWeights() {
            let weightsSet = false;
            let weightsSum = 0;

            this.goal.keyResults.forEach(keyResult => {
                if (keyResult.type === "initiative" || keyResult._id === this.kr._id) {
                    return;
                }

                const metricWeight =
                    keyResult.metricWeight &&
                    parseFloat(keyResult.metricWeight.toString().replace(",", "."));

                if (metricWeight && !isNaN(metricWeight) && isFinite(metricWeight)) {
                    weightsSet = true;
                    weightsSum += metricWeight;
                }
            });

            if (this.kr.metricWeight) {
                weightsSet = true;
                weightsSum += parseFloat(this.kr.metricWeight.toString().replace(",", "."));
            }

            this.$emit("warning", {
                type: "weight",
                value: Boolean(weightsSet && weightsSum !== 100),
            });
        },
        typeaheadOwnerOnSelect(owner) {
            if (owner.type === "team") {
                this.kr.ownerTeam = owner;
                this.kr.ownerUser = null;
            } else if (owner.type === "user") {
                this.kr.ownerTeam = null;
                this.kr.ownerUser = owner;
            }

            this.kr.owner = owner.name;

            this.$store.dispatch("users/search/reset");
            this.$store.dispatch("teams/search/reset");
        },
        typeaheadOwnerOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
                this.$store.dispatch("teams/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
                this.$store.dispatch("teams/search/reset");
            }
        },
        removeOwner() {
            this.kr.owner = "";
            this.kr.ownerTeam = null;
            this.kr.ownerUser = null;

            this.showOwner = true;
        },
    },
};
</script>

<style scoped>
.l-goal-details-key-result-edit .btn-group {
    margin-bottom: 8px;
}
</style>
